import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Grid,
  Typography,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  TrendingUp,
  AttachMoney,
  BarChart as BarChartIcon,
  PieChart,
} from "@mui/icons-material";
import { barClubViewSearchByWholesalerIdViewAllRequest } from "../redux/barclub/BarClubAction";
import { useDispatch, useSelector } from "react-redux";
import { wholesalerProductSearchViewByWholesalerIdRequest } from "../redux/wholesalerProduct/WholesalerProductAction";
import { productQuantitySearchViewAllRequest } from "../redux/productQuantity/ProductQuantityAction";
import {
  alcoholPercentageViewRequest,
  WholesalerAlcoholPercentageViewRequest,
} from "../redux/typeOfAlcohol/TypeOfAlcoholAction";
import { APP_LINKS } from "./global/wholeSalerMenuItems";
import { wholesalerDashboardStyles } from "./components/reportStyles";
import {
  wholesalerReportByIdSearchRequest,
  wholesalerReportMonthByIdRequest,
} from "../redux/wholesalerReportById/WholesalerReportByIdAction";
import { format, parseISO, isValid } from "date-fns";
import { CircleLoader } from "react-spinners";

const WholesalerDashboard = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [showScroll, setShowScroll] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  const [monthlyTransactionData, setMonthlyTransactionData] = useState([]);

  const id = useSelector(
    (state) => state?.values?.loginValues?.[0]?.wholesaler_id
  );

  const barClubViewAllState = useSelector(
    (state) => state?.barClub?.viewAllSearchBarClubByWholesalerId || {}
  );
  const barClubList = barClubViewAllState?.data?.data || [];
  const barTotalRecords = barClubViewAllState?.data?.totalRecords || 0;

  useEffect(() => {
    if (id) {
      dispatch(barClubViewSearchByWholesalerIdViewAllRequest(id));
    }
  }, [dispatch, id]);

  const viewAllProductQuantitiesState = useSelector(
    (state) => state?.productQuantity?.viewAllSearchProductQuantities || {}
  );
  const productQuantityRecords =
    viewAllProductQuantitiesState?.data?.totalRecords || 0;

  useEffect(() => {
    dispatch(productQuantitySearchViewAllRequest());
  }, [dispatch]);

  const wholesalerProductViewAllState = useSelector(
    (state) =>
      state?.wholesalerProduct?.viewWholesalerProductSearchByWholesalerId || {}
  );
  const wholesalerProductList = wholesalerProductViewAllState?.data || [];
  const wholesalerProductTotalRecords =
    wholesalerProductViewAllState?.totalRecords || 0;

  useEffect(() => {
    if (id) {
      dispatch(wholesalerProductSearchViewByWholesalerIdRequest(id));
    }
  }, [dispatch, id]);

  const alcoholPercentageViewState = useSelector(
    (state) => state?.TypeAlcohol?.viewWholesalerAlcoholPercentage || {}
  );

  const alcoholPercentageList = alcoholPercentageViewState?.data?.data || [];
  useEffect(() => {
    dispatch(WholesalerAlcoholPercentageViewRequest());
  }, [dispatch, id]);

  const wholesalerReportViewAllState = useSelector(
    (state) => state?.WholesalerReportById?.searchWholesalerReportsById || {}
  );

  const wholesalerReportList =
    wholesalerReportViewAllState?.data?.dayWiseReport || [];

  const preprocessData = (data) => {
    if (!Array.isArray(data)) return [];

    const processed = data.map((item, index) => ({
      id: index + 1,
      Transactions: item?.transactions || 0,
      BottlesSold: Number(item?.totalBottlesSold) || 0,
      MostSoldBrand: item?.mostOrderedBrand || "Unknown",
      Date: item?.date || "N/A",
    }));

    return processed;
  };

  useEffect(() => {
    if (id) {
      dispatch(wholesalerReportByIdSearchRequest(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (wholesalerReportViewAllState?.success) {
      setProcessedData(preprocessData(wholesalerReportList));
    }
  }, [wholesalerReportList, wholesalerReportViewAllState]);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const wholesalerMonthReportViewAllState = useSelector(
    (state) => state?.WholesalerReportById?.monthWholesalerReportsById || {}
  );
  useEffect(() => {
    if (id) {
      dispatch(wholesalerReportMonthByIdRequest(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (wholesalerMonthReportViewAllState?.success) {
      const monthlyData =
        wholesalerMonthReportViewAllState?.data?.data?.monthWiseReport || [];

      const transactionsMap = {};
      const bottlesSoldMap = {};

      monthlyData.forEach((item) => {
        const [year, monthNum] = item.month.split("-");
        const monthIndex = parseInt(monthNum) - 1;
        const monthName = new Date(year, monthIndex).toLocaleString("default", {
          month: "short",
        });

        transactionsMap[monthName] = item.monthlyTransactions;
        bottlesSoldMap[monthName] = item.totalBottlesSold;
      });

      const formattedData = months.map((month) => ({
        month,
        monthlyTransactions: transactionsMap[month] || 0,
        totalBottlesSold: bottlesSoldMap[month] || 0,
      }));

      setMonthlyTransactionData(formattedData);
    }
  }, [wholesalerMonthReportViewAllState]);

  const handleTableClick = (e) => {
    e.stopPropagation();
    setShowScroll(true);
  };

  const handleOutsideClick = () => {
    setShowScroll(false);
  };

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const todayString = getTodayString();

  const todayTransactions = wholesalerReportList
    .reduce((acc, item) => {
      if (item.date === todayString) {
        return acc + (parseFloat(item.transactions) || 0);
      }
      return acc;
    }, 0)
    .toFixed(2);

  const statPaths = {
    Products: APP_LINKS.PRODUCTLIST,
    ProductStock: APP_LINKS.PRODUCTQUANTITYLIST,
    Retailers: APP_LINKS.BARCLUBAGGREGATION,
    Sales: APP_LINKS.WHOLESALERREPORT,
  };

  const stats = {
    Products: wholesalerProductTotalRecords,
    ProductStock: productQuantityRecords,
    Retailers: barTotalRecords,
    Sales: todayTransactions,
  };

  const wholesalerProductLoading = wholesalerProductViewAllState?.inProgress;
  const quantityLoading = viewAllProductQuantitiesState?.inProgress;
  const barLoading = barClubViewAllState?.inProgress;
  const salesLoading = wholesalerReportViewAllState?.inProgress;

  const statLoadingMap = {
    Products: wholesalerProductLoading,
    ProductStock: quantityLoading,
    Retailers: barLoading,
    Sales: salesLoading,
  };

  const alcoholColorMap = {
    Beer: "#3182ce",
    Wine: "#9f7aea",
    Spirits: "#48bb78",
    Cider: "#ff7f0e",
    "Soft Drinks": "#ffbb78",
    Other: "#d62728",
    Tequila: "#ffa726",
    Vodka: "#42a5f5",
    Whiskey: "#ab47bc",
  };

  const statIcons = [
    <AttachMoney />,
    <TrendingUp />,
    <BarChartIcon />,
    <PieChart />,
  ];

  return (
    <div onClick={handleOutsideClick}>
      <Box sx={wholesalerDashboardStyles.dashboardHeading}>
        <Box
          className="headingTable"
          sx={wholesalerDashboardStyles.headingTableDashboard}
        >
          <h1> Wholesaler Dashboard</h1>
        </Box>
        {/* Statistics Overview */}
        <Grid container spacing={3} mb={4}>
          {Object.entries(stats).map(([key, value], index) => {
            const path = statPaths[key];
            const isBlue = index % 2 !== 0;
            const bgColor = isBlue ? "#3182ce" : "#ffffff";
            const textColor = isBlue ? "#ffffff" : "#2f4cdd";
            const isLoading = statLoadingMap[key]; 

            return (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    color: textColor,
                    bgcolor: bgColor,
                    cursor: path ? "pointer" : "default",
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: path ? "scale(1.03)" : "none",
                    },
                  }}
                  onClick={() => path && navigate(path)}
                >
                  <Box display="flex" justifyContent="center" mb={1}>
                    {statIcons[index % statIcons.length]}
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    {key.replace(/([A-Z])/g, " $1")}
                  </Typography>

                  {isLoading ? (
                    <Box display="flex" justifyContent="center">
                      <CircleLoader size={28} color={textColor} />
                    </Box>
                  ) : (
                    <Typography variant="h5" color={textColor}>
                      {typeof value === "number"
                        ? value.toLocaleString()
                        : value}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Advanced Sales Visualization */}
        <Grid container spacing={3}>
          {/* Comprehensive Sales Chart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={wholesalerDashboardStyles.salesPaper}>
              <Typography
                variant="h3"
                color="#7894F7"
                gutterBottom
                sx={wholesalerDashboardStyles.salesTypography}
              >
                Sales Performance Metrics
              </Typography>

              {wholesalerMonthReportViewAllState?.inProgress ? (
                <Box
                  height={300}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircleLoader size={50} color="#0458d3" />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={monthlyTransactionData}>
                    <CartesianGrid stroke="#e0e0e0" strokeDasharray="5 5" />
                    <XAxis dataKey="month" stroke="#757575" />
                    <YAxis stroke="#757575" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "monthlyTransactions")
                          return [`${value}`, "Transactions"];
                        if (name === "totalBottlesSold")
                          return [`${value}`, "Bottles Sold"];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="monthlyTransactions"
                      barSize={20}
                      fill="#3182ce"
                      name="Transactions"
                    />
                    <Bar
                      dataKey="totalBottlesSold"
                      barSize={20}
                      fill="#9f7aea"
                      name="Bottles Sold"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Alcohol Categories Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={wholesalerDashboardStyles.productPaper}>
              <Typography
                variant="h3"
                color="#7894F7"
                gutterBottom
                sx={wholesalerDashboardStyles.producTypography}
              >
                Alcohol Categories
              </Typography>

              {alcoholPercentageViewState?.inProgress ? (
                <Box
                  height={300}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircleLoader size={50} color="#0458d3" />
                </Box>
              ) : (
                <Box mt={3} sx={wholesalerDashboardStyles.productFlex}>
                  {[...alcoholPercentageList]
                    .sort(
                      (a, b) =>
                        parseFloat(b.percentage) - parseFloat(a.percentage)
                    )
                    .slice(0, 5)
                    .map((category) => {
                      const color =
                        alcoholColorMap[category.type_of_alcohol] || "#7894F7";
                      return (
                        <Box key={category.type_of_alcohol_id} mb={2}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <Typography variant="body2" color="textSecondary">
                              {category.type_of_alcohol}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {category.percentage}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(category.percentage)}
                            sx={{
                              height: 8,
                              borderRadius: 5,
                              bgcolor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: color,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Product List Table */}
        <Box mt={4}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={wholesalerDashboardStyles.listProductsPaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={wholesalerDashboardStyles.listTypographyColor}
              >
                Product List
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={wholesalerDashboardStyles.listTableHead}>
                    <TableRow>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Type Of Alcohol
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Brand
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Sub Brand Name
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Capacity
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Price
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wholesalerProductViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={40} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(wholesalerProductList) &&
                      wholesalerProductList.length > 0 ? (
                      wholesalerProductList.slice(0, 5).map((data) => (
                        <TableRow key={data?.id}>
                          <TableCell>
                            {data?.ProductAlcoholType?.alcohol_type || "N/A"}
                          </TableCell>
                          <TableCell>
                            {data?.Brand?.brand_name || "N/A"}
                          </TableCell>
                          <TableCell>
                            {data?.Product?.product_name || "N/A"}
                          </TableCell>
                          <TableCell>{data?.quantity || "N/A"}</TableCell>
                          <TableCell>
                            {data?.price
                              ? Number(data.price).toFixed(2)
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          style={wholesalerDashboardStyles.listTableRow}
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Box>
        <Grid container spacing={1} mt={3}>
          {/* Bar Inventory Table */}
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={wholesalerDashboardStyles.listPaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={wholesalerDashboardStyles.listTypographyColor}
              >
                Bar Inventory
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={wholesalerDashboardStyles.listTableHead}>
                    <TableRow>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Register No
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Name
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Bar/Club
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Contact
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {barClubViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={40} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(barClubList) && barClubList.length > 0 ? (
                      barClubList.slice(0, 5).map((data) => (
                        <TableRow key={data?.id}>
                          <TableCell>{data?.reg_no || "N/A"}</TableCell>
                          <TableCell>
                            {data?.first_name + " " + data?.last_name || "N/A"}
                          </TableCell>
                          <TableCell>{data?.name || "N/A"}</TableCell>
                          <TableCell>{data?.contact || "N/A"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          style={wholesalerDashboardStyles.listTableRow}
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Sales Table */}
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={wholesalerDashboardStyles.listPaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={wholesalerDashboardStyles.listTypographyColor}
              >
                Sales Data
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={wholesalerDashboardStyles.listTableHead}>
                    <TableRow>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Amount
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        BottlesSold
                      </TableCell>
                      <TableCell sx={wholesalerDashboardStyles.listTableCell}>
                        Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wholesalerReportViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={40} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : processedData.length > 0 ? (
                      processedData.slice(0, 5).map((data) => (
                        <TableRow key={data.id}>
                          <TableCell>
                            {Number(data.Transactions).toFixed(2) || "N/A"}
                          </TableCell>
                          <TableCell>{data.BottlesSold || "N/A"}</TableCell>
                          <TableCell>
                            {data.Date && isValid(parseISO(data.Date))
                              ? format(parseISO(data.Date), "dd MMM yyyy")
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          style={wholesalerDashboardStyles.listTableRow}
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default WholesalerDashboard;
